<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'

import { Icon } from '@iconify/vue'

import MemberCollect from '@/components/admin/company-manage/MemberCollect.vue'
import LoadingSpinner from '@/components/UI/LoadingSpinner.vue'
import UserAvatar from '@/components/UI/UserAvatar.vue'

import { getProtectedImage } from '@/utils/api/fetchAuthImage'
import { notifySuccess, notifyError, notifyWarning } from '@/utils/ui/handleNotification'

import * as aggr from '@/api-generator/aggr'
import { feCompaniesAPI, companyOrganizationAPI, userMgmtProfileAPI } from '@/APIs'

import type { Organization } from '@/types/organization'
import type { AxiosResponse } from 'axios'

interface Props {
  organizationData: Organization
  isDisplayedOnFrontend?: boolean
}

const { organizationData, isDisplayedOnFrontend = false } = defineProps<Props>()

const isLoading = ref<boolean>(false)

interface CustomerEmailInviteFormModel {
  memberList: { userUuid?: string; email?: string }[]
}

const emailInviteForm = ref<CustomerEmailInviteFormModel>({
  memberList: []
})

// 初始化表單
const initForm = () => {
  members.value = []
  emailInviteForm.value.memberList = []
}

// 格式化邀請名單
const prepareInviteList = () => {
  return emailInviteForm.value.memberList.map((item) => {
    return item.userUuid ? { userUuid: item.userUuid } : { email: item.email }
  })
}

// 傳送邀請信
const sendEmailInviteBase = async <T,>(
  reqBody: T,
  sendInviteFunction: (reqBody: T) => Promise<AxiosResponse<void, any>>,
  queryCallback: () => Promise<void>,
  notify: { title?: string; message: string }
) => {
  isLoading.value = true
  try {
    await sendInviteFunction(reqBody)
    await queryCallback()
    emailInviteForm.value.memberList = []
    notifySuccess(notify.message, notify.title ?? '發送成功')
  } catch (error) {
    console.error(error)
  } finally {
    isLoading.value = false
  }
}

// 前後台 API 設定
const contextConfig = isDisplayedOnFrontend
  ? {
      queryMembers: feCompaniesAPI.queryOrganizationMembers,
      sendInvite: feCompaniesAPI.sendOrganizationRegistrationInvitation,
      inviteNotify: { title: '邀請已送出', message: '相關成員將收到加入通知' }
    }
  : {
      queryMembers: companyOrganizationAPI.queryMembers,
      sendInvite: companyOrganizationAPI.sendRegistrationInvitation,
      inviteNotify: { message: '已發送邀請信' }
    }

const handleQueryOrgMembers = async () => {
  const reqBody = {
    companyUuid: organizationData.companyUuid,
    companyOrganizationUuid: organizationData.companyOrganizationUuid
  }
  await handleQueryOrgMembersBase(reqBody, contextConfig.queryMembers)
}

const sendEmailInvite = async () => {
  const list = prepareInviteList()
  const reqBody = {
    companyUuid: organizationData.companyUuid,
    companyOrganizationUuid: organizationData.companyOrganizationUuid,
    orgMembers: list
  }
  await sendEmailInviteBase(reqBody, contextConfig.sendInvite, handleQueryOrgMembers, contextConfig.inviteNotify)
}

const canSubmit = computed(() => {
  return emailInviteForm.value.memberList.length > 0 && !isLoading.value
})

/** 已存在的成員 email 集合 */
const existingMemberEmails = computed(() => {
  return new Set(members.value.map((member) => member.email).filter(Boolean) as string[])
})

interface Member extends aggr.OrganizationQueryMembersRs, aggr.FECompanyOrganizationQueryMembersRs {
  blobImg?: string
}
const members = ref<Member[]>([])
const isMembersLoading = ref(false)

// 取得組織成員
const handleQueryOrgMembersBase = async <
  T extends aggr.OrganizationQueryMembersRq | aggr.FECompanyOrganizationQueryMembersRq
>(
  reqBody: T,
  queryFunction: (req: T) => Promise<{ data: Member[] }>
) => {
  isMembersLoading.value = true
  try {
    const { data } = await queryFunction(reqBody)
    members.value = data
    await Promise.allSettled(
      members.value.map(async (item) => {
        if (item.photoUrl) {
          item.blobImg = await getProtectedImage(item.photoUrl)
        }
      })
    )
  } catch (error) {
    console.error(error)
  } finally {
    isMembersLoading.value = false
  }
}

const roleType = (roleCode: number | undefined) => {
  switch (roleCode) {
    case aggr.OrganizationMemberRole.Leader:
      return '組長'
    case aggr.OrganizationMemberRole.Member:
      return '組員'
    default:
      return ''
  }
}

const changePermission = async (member: Member, role: aggr.OrganizationMemberRole) => {
  const name = member.userName ? member.userName : member.email
  if (member.memberRoleCode === role) {
    return
  }
  try {
    await companyOrganizationAPI.updateMemberPermission({
      companyUuid: organizationData.companyUuid,
      companyOrganizationUuid: organizationData.companyOrganizationUuid,
      userUuid: member.userUuid,
      memberRoleCode: role
    })
    member.memberRoleCode = role
    notifySuccess(`${name} 變更權限成功`)
  } catch (error: any) {
    const errorMsg = error.response?.data?.message ?? error.message
    console.error(errorMsg)
  }
}

// 重發認證信冷卻機制，避免短時間內重複寄送
const RESEND_COOLDOWN = 30
const resendCoolDownMap = ref<Map<string, number>>(new Map())
const coolDownTimers: ReturnType<typeof setInterval>[] = []

const startResendCoolDown = (userUuid: string) => {
  resendCoolDownMap.value.set(userUuid, RESEND_COOLDOWN)
  const timer = setInterval(() => {
    const remaining = resendCoolDownMap.value.get(userUuid)
    if (remaining !== undefined && remaining > 1) {
      resendCoolDownMap.value.set(userUuid, remaining - 1)
    } else {
      resendCoolDownMap.value.delete(userUuid)
      clearInterval(timer)
    }
  }, 1000)
  coolDownTimers.push(timer)
}

onUnmounted(() => {
  coolDownTimers.forEach(clearInterval)
})

const resendInviteEmail = async (member: Member) => {
  if (!member.userUuid) {
    return
  }
  const remaining = resendCoolDownMap.value.get(member.userUuid)
  if (remaining) {
    notifyWarning(`為避免重複寄送，請於 ${remaining} 秒後再試。`, '暫時無法重新寄送')
    return
  }
  try {
    const reqBody: aggr.UserResendConfirmEmailRq = {
      userUuids: [member.userUuid]
    }
    await userMgmtProfileAPI.sendConfirmEmail(reqBody)
    notifySuccess('已重發認證信', '發送成功')
    startResendCoolDown(member.userUuid)
  } catch (error) {
    console.error(error)
    notifyError('重發認證信失敗')
  }
}

const removeMember = async (member: Member) => {
  const name = member.userName ? member.userName : member.email
  try {
    await companyOrganizationAPI.deleteMember({
      companyUuid: organizationData.companyUuid,
      companyOrganizationUuid: organizationData.companyOrganizationUuid,
      userUuid: member.userUuid
    })
    const index = members.value.findIndex((item) => item.userUuid === member.userUuid)
    if (index !== -1) {
      members.value.splice(index, 1)
    }
    notifySuccess(`${name} 移除成功`)
  } catch (error: any) {
    const errorMsg = error.response?.data?.message ?? error.message
    console.error(errorMsg)
  }
}

onMounted(async () => {
  initForm()
  await handleQueryOrgMembers()
})
</script>

<template>
  <div class="px-6">
    <el-form :model="emailInviteForm" :label-position="'top'">
      <MemberCollect
        v-model:list="emailInviteForm.memberList"
        label="透過電子郵件邀請"
        :required="false"
        :type="'input'"
        :identity-type="aggr.IdentityPickerType.CompanyOwner"
        :collect-in-frontend="true"
        :auto-focus="true"
        :existing-member-emails="existingMemberEmails"
      />
      <div class="text-xs text-app-black-600">該成員會收到一封電子郵件，當中提供您組織的組員存取權。</div>
    </el-form>
    <div class="action-row">
      <button
        type="button"
        class="flex h-9 items-center rounded-4xl bg-app-brand-blue-500 px-6 py-2 text-white disabled:bg-app-brand-blue-500/50"
        :disabled="!canSubmit"
        @click="sendEmailInvite"
      >
        邀請
      </button>
    </div>
    <div class="members">
      <div class="header">
        <div class="text-sm font-bold leading-6 text-app-black-600">已邀請成員</div>
      </div>
      <div class="member-list">
        <template v-if="isMembersLoading">
          <div class="member-loading">
            <LoadingSpinner />
          </div>
        </template>
        <template v-else>
          <div v-for="(member, index) in members" :key="index" class="member-item">
            <div class="member-data">
              <UserAvatar :src="member.blobImg" :size="40" />
              <div class="detail">
                <div class="name">{{ member.userName }}</div>
                <div class="email">
                  {{ member.email }}
                  <span
                    v-if="member.userStatusCode === aggr.UserStatus.Inactive"
                    class="rounded-xl bg-app-black-50 px-1.5 py-0.5 text-xs text-app-black-500"
                    >待驗證
                  </span>
                </div>
              </div>
            </div>
            <div class="type">
              <el-dropdown trigger="click">
                <div
                  class="flex items-center rounded-[4px] pb-1 pl-1.5 pr-1 pt-1 text-app-black-1000 hover:bg-app-black-50"
                >
                  <div class="text-sm font-normal leading-5">
                    {{ roleType(member.memberRoleCode) }}
                  </div>
                  <span class="p-[3px]">
                    <Icon icon="ri:arrow-down-s-line" width="14px" height="14px" />
                  </span>
                </div>
                <template #dropdown>
                  <el-dropdown-menu class="user-role-menu">
                    <el-dropdown-item
                      :class="{
                        'active-role': member.memberRoleCode === aggr.OrganizationMemberRole.Leader
                      }"
                      @click="changePermission(member, aggr.OrganizationMemberRole.Leader)"
                    >
                      組長
                    </el-dropdown-item>
                    <el-dropdown-item
                      :class="{
                        'active-role': member.memberRoleCode === aggr.OrganizationMemberRole.Member
                      }"
                      @click="changePermission(member, aggr.OrganizationMemberRole.Member)"
                    >
                      組員
                    </el-dropdown-item>
                    <el-dropdown-item
                      v-if="member.userStatusCode === aggr.UserStatus.Inactive"
                      @click="resendInviteEmail(member)"
                    >
                      重發認證信
                    </el-dropdown-item>
                    <el-dropdown-item @click="removeMember(member)">
                      <span class="color-danger">從組織移除</span>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.action-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
  .submit-btn {
    color: #ffffff;
  }
}

.members {
  margin-top: 40px;

  .header {
    display: flex;
    justify-content: space-between;

    .member {
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
      letter-spacing: 1.147px;
      color: #000;
    }

    .type {
      font-size: 12px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
      letter-spacing: 1.147px;
      color: var(--gray-7d);
      padding-right: 44px;
    }
  }

  .member-list {
    margin-top: 18px;
    .member-item {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .member-data {
        display: flex;

        .detail {
          margin-left: 12px;
          display: flex;
          flex-direction: column;
          justify-content: center;

          .name {
            font-size: 14px;
            font-style: normal;
            font-weight: 400;
            line-height: normal;
            color: #000;
          }

          .email {
            font-size: 12px;
            font-style: normal;
            font-weight: 400;
            line-height: normal;
            color: var(--gray-7d);

            .tag {
              font-size: 12px;
              font-style: normal;
              font-weight: 400;
              line-height: normal;
              margin-left: 4px;
              color: var(--danger);
            }
          }
        }
      }

      .type {
        display: flex;
        align-items: center;

        .text {
          font-size: 12px;
          font-style: normal;
          font-weight: 400;
          line-height: normal;
          color: var(--gray-7d);
        }
      }
    }

    .member-item:not(:last-child) {
      margin-bottom: 22px;
    }

    .type-select {
      width: 80px;
    }
    .member-loading {
      display: flex;
      justify-content: center;
    }
  }
}
</style>
